import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnChanges, OnInit, Signal, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DropdownCountryComponent } from '@lx/shared/ui/dropdown-country';
import { LoadingInfo, LoadingService, LoadingSpinnerComponent } from '@lx/shared/ui/loading-spinner';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { Company, CompanyEnum } from '../../models/company.model';
import { LangDirection } from '../../models/lang-direction';
import { PriceListTermsAndConditions } from '../../models/pricelist-terms-and-conditions';
import { PriceListTermsLanguage } from '../../models/pricelist-terms-language.mode';
import { PriceListService } from '../../services/pricelist.service';
import { SharedDataStore } from '../../store';

@Component({
  standalone: true,
  imports: [CommonModule, ProgressSpinnerModule, DropdownCountryComponent, LoadingSpinnerComponent],
  selector: 'lx-price-lists-portal-terms-and-conditions',
  templateUrl: './terms-and-conditions.component.html',
  styleUrls: ['./terms-and-conditions.component.scss', './print-terms-and-conditions.component.scss'],
})
export class TermsAndConditionsComponent implements OnInit, OnChanges {
  @Input() company: Company | undefined;

  selectedLanguage = '';
  termsAndConditions$: Observable<PriceListTermsAndConditions> | undefined;
  dir = LangDirection.LTR;

  termsAndConditionsLanguages$: Observable<PriceListTermsLanguage[]> | undefined;
  protected loadingInfo: Signal<LoadingInfo>;
  sanitizedTermsOfConditionContent$: Observable<SafeHtml> | undefined;

  private readonly DEFAULT_SELECTED_LANGUAGE = 'en-US';
  private readonly priceListService = inject(PriceListService);
  private readonly sharedDataStore = inject(SharedDataStore);
  private readonly loadingService = inject(LoadingService);
  private readonly sanitizer = inject(DomSanitizer);

  ngOnInit(): void {
    this.loadingInfo = this.loadingService.loading;
    this.selectedLanguage = this.DEFAULT_SELECTED_LANGUAGE;
    this.termsAndConditions$ = this.company === CompanyEnum.KIL ? this.sharedDataStore.termsAndConditionsKIL$ : this.sharedDataStore.termsAndConditionsALP$;
    this.sanitizedTermsOfConditionContent$ = this.getSanitizedTermsOfConditionContent(this.termsAndConditions$);
  }

  ngOnChanges({ company }: SimpleChanges): void {
    if (company && company.currentValue) {
      this.termsAndConditionsLanguages$ = this.sharedDataStore.termsAndConditionsLanguagesForCompany(company.currentValue);
    }
  }

  loadTermsAndConditionsForSelectedLanguage({ value }): void {
    this.selectedLanguage = value;
    this.dir = value === 'ar-SA' ? LangDirection.RLT : LangDirection.LTR;
    this.termsAndConditions$ = this.priceListService.getTermsAndConditions(this.company, this.selectedLanguage).pipe(
      tap(() => this.loadingService.stopLoading()),
      catchError(() => {
        return of(undefined);
      }),
    );

    this.sanitizedTermsOfConditionContent$ = this.getSanitizedTermsOfConditionContent(this.termsAndConditions$);
  }

  getSanitizedTermsOfConditionContent(priceListTermsAndConditions$: Observable<PriceListTermsAndConditions>): Observable<SafeHtml> {
    return priceListTermsAndConditions$.pipe(map((termsAndConditions) => this.sanitizer.bypassSecurityTrustHtml(termsAndConditions.content)));
  }
}
